<template>
  <div>
    <div
      v-if="requestList.length"
      class="table-responsive"
    >
      <table class="table table-bordered table-striped table-vcenter">
        <thead class="table-dark">
          <tr>
            <th>T&C</th>
            <th>
              {{ editable ? "Update Payment" : "Paid Status" }}
            </th>
            <th>Remarks</th>
            <th>Project Title</th>
            <th>Name</th>
            <th>Phone Number</th>
            <th>Primary Email Address</th>
            <th>Secondary Email Address</th>
            <th v-if="editable" />
            <th>Update History</th>
            <th v-if="isAdmin">Invoice</th>
          </tr>
        </thead>
        <tbody
          v-for="request in requestList"
          :key="request.id"
        >
          <tr>
            <!-- expert terms and conditions column -->
            <td>
              <expert-terms-condition-badge
                :expert="request.assignment.expert"
              />
              <expert-terms-reminder-button
                :expert="request.assignment.expert"
                :project-title="request.project.project_title"
                :project-id="request.project_id"
              />
            </td>

            <!--                  update payment column-->
            <td>
              <!-- Button trigger modal -->
              <div v-if="editable">
                <PaymentUpdateModal
                  :request="request"
                  @payment-updated="fetchRequests"
                />
              </div>
              <div v-else>
                {{ getRequestPaidStatus(request) ? "Paid" : "Unpaid" }}
              </div>
            </td>

            <!-- Remarks Column -->
            <td>
              {{ request.remarks }}
            </td>

            <!--                  project title column-->
            <td>
              <a
                :href="request.project.admin_detail_url"
                target="_blank"
              >{{ request.project.project_title }}</a>
            </td>

            <!--                  expert name column-->
            <td>
              <a
                :href="request.assignment.expert.admin_profile_url"
                target="_blank"
              >{{ request.assignment.expert.user.full_name }}</a>
            </td>

            <!-- expert phone number column -->
            <td>
              {{
                request.assignment.expert.user.full_mobile_phone && request.assignment.expert.user.full_mobile_phone !== "" ? "+" + request.assignment.expert.user.full_mobile_phone : "N/A"
              }}
            </td>

            <!--                  expert primary email address column-->
            <td>{{ request.assignment.expert.user.email }}</td>

            <!--                  expert secondary email address column-->
            <td>
              {{ request.assignment.expert.user.email_2 }}
            </td>

            <!--                  edit request column-->
            <td v-if="editable">
              <button
                class="btn btn-info"
                @click="showRequestModal(request)"
              >
                Edit
              </button>
            </td>

            <!--                  update history column-->
            <td>
              <div>
                <button
                  v-b-modal="`request-log-modal-${request.id}`"
                  class="btn btn-sm btn-info"
                  @click="openLogModal(request.id)"
                >
                  Show history
                </button>

                <b-modal
                  :id="`request-log-modal-${request.id}`"
                  title="Request update history"
                  class="modal"
                  tabindex="-1"
                >
                  <template #modal-header="{ close }">
                    <h5>Request update history</h5>
                    <button
                      type="button"
                      class="btn-close"
                      @click="close()"
                    />
                  </template>
                  <b-container>
                    <payment-request-update-logs
                      :request-logs="getRequestLogs(request)"
                    />
                  </b-container>
                </b-modal>
              </div>
            </td>

            <td v-if="isAdmin">
              <button
                v-if="getRequestPaidStatus(request)"
                type="button"
                class="btn btn-sm btn-outline-secondary px-2"
                title="Export Invoice"
                @click="showInvoiceModal(request)"
              >
                <i class="fa fa-file-invoice-dollar" /> Export Invoice
              </button>
            </td>
          </tr>
          <tr>
            <td :colspan="tableColspan">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Call Duration</th>
                    <th>Hourly Rate</th>
                    <th>Client Duration</th>
                    <th>Client Hourly Rate</th>
                    <th>Payment to Expert</th>
                    <th>Within 48 hours</th>
                    <th>Screenshots</th>
                    <th>Follow up</th>
                    <th>Staff Credits</th>
                    <th>Requestor</th>
                    <th>Hourly Credits to Charge Client</th>
                    <th>Head Hunting</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <!--                  request date column-->
                    <td>{{ $moment(request.requested_time).format("DD MMM YYYY") }}</td>
                    <td>{{ request.duration !== null ? `${request.duration} minutes` : "" }}</td>
                    <td>{{ request.hourly_rate !== null ? `${request.hourly_rate} USD` : "" }}</td>
                    <td>{{ request.client_duration !== null ? `${request.client_duration} minutes` : "" }}</td>
                    <td>{{ request.client_hourly_rate !== null ? `${request.client_hourly_rate} ${request.client_rate_currency}` : "" }}</td>
                    <td>
                      {{ request.payment_to_expert > 0 ? `${request.payment_to_expert_formatted} USD` : ""
                      }}
                    </td>
                    <td>{{ request.in_hours === 1 ? "Yes" : "No" }}</td>
                    <!-- 48-hour screenshots column -->
                    <td>
                      <template v-if="request.in_hours === 1 && getScreenshotUrls(request).length">
                        <div
                          v-for="(url, idx) in getScreenshotUrls(request)"
                          :key="`${request.id}-screenshot-${idx}`"
                        >
                          <a
                            :href="url"
                            target="_blank"
                            rel="noopener noreferrer"
                          >Screenshot {{ idx + 1 }}</a>
                        </div>
                      </template>
                      <span
                        v-else
                        class="text-muted"
                      >None</span>
                    </td>
                    <td>{{ request.follow_up_consult === 1 ? "Yes" : "No" }}</td>
                    <td>{{ request.credits }}</td>
                    <td>{{ request.requestor }}</td>
                    <td>{{ request.client_hourly_credit }}</td>
                    <td>{{ request.head_hunting === 1 ? "Yes" : "No" }}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <div class="alert alert-info">
        No requests found
      </div>
    </div>
    <!-- eof request list -->
    <!-- BOF edit request modal -->
    <div
      id="edit-request-modal"
      ref="editRequestModal"
      class="modal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Requested Payments
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <form
            ref="creditForm"
            @submit="submitCreditForm"
          >
            <div class="modal-body">
              <div class="mb-3">
                <label
                  for="creditInput"
                  class="form-label"
                >Credits</label>
                <input
                  id="creditInput"
                  v-model="selectedCredits"
                  v-no-wheel
                  type="number"
                  step="0.1"
                  min="0"
                  required
                  name="credits"
                  class="form-control"
                  aria-describedby="emailHelp"
                >
              </div>

              <div class="mb-3 form-check">
                <input
                  id="inHourCheck"
                  v-model="selectedInHour"
                  type="checkbox"
                  name="in_hours"
                  class="form-check-input"
                >
                <label
                  class="form-check-label"
                  for="inHourCheck"
                >is 48 hours</label>
              </div>

              <input
                type="hidden"
                name="request_id"
                :value="selectedRequest.id"
              >
            </div>
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="updatingCredits"
              >
                Update
                <div
                  v-if="updatingCredits"
                  class="spinner-grow spinner-grow-sm"
                  role="status"
                >
                  <span class="visually-hidden">Loading...</span>
                </div>
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <!-- EOF edit request modal -->
    <invoice-export-modal
      v-if="isAdmin && activeInvoiceExport"
      ref="invoiceExportModal"
      :key="`${activeInvoiceExport.projectId}-${activeInvoiceExport.expertId}`"
      :project-id="activeInvoiceExport.projectId"
      :expert-id="activeInvoiceExport.expertId"
    />
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import moment from "moment-timezone";
import PaymentUpdateModal from "./PaymentUpdateModal.vue";
import ExpertTermsConditionBadge from "../../expert/ExpertTermsConditionBadge.vue";
import InvoiceExportModal from "../../project/InvoiceExportModal.vue";

export default {
    name: "PaymentRequestListTable",
    components: { PaymentUpdateModal, ExpertTermsConditionBadge, InvoiceExportModal },
    props: {
        isAdmin: {
            type: Boolean,
            default: false,
        },
        requestList: {
            type: Array,
            required: true,
        },
        editable: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data: function() {
        return {
            updatingCredits: false,
            selectedRequest: {},
            selectedCredits: null,
            selectedInHour: false,
            // requestUpdatingStatuses: [],
            paymentUpdateModal: null,
            activeInvoiceExport: null,
        };
    },
    computed: {
        editRequestModal() {
            return new Modal(this.$refs.editRequestModal);
        },
        tableColspan() {
            let count = 9;
            if (this.editable) {
                count++;
            }
            if (this.isAdmin) {
                count++;
            }
            return count;
        },
    },
    methods: {
        fetchRequests() {
            return this.$emit("fetch-requests");
        },
        showRequestModal(request) {
            this.selectedRequest = request;
            this.selectedCredits = request.credits;
            this.selectedInHour = request.in_hours === 1;
            this.editRequestModal.show();
        },
        getRequestPaidStatus(request) {
            return request.paid === 1;
        },
        getScreenshotUrls(request) {
            return Array.isArray(request?.screenshot_urls) ? request.screenshot_urls : [];
        },
        showInvoiceModal(request) {
            this.activeInvoiceExport = {
                projectId: request.project_id,
                expertId: request.assignment.expert.id,
            };
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.$refs.invoiceExportModal?.show();
                });
            });
        },
        /*setPaymentStatus(request) {
            const status = request.paid === 1 ? 0 : 1;
            const requestId = request.id;
            const paymentUrl = document.getElementById(`payment-url-${requestId}`).value;

            this.requestUpdatingStatuses.push(requestId);
            this.$store.dispatch("credit/updateRequestStatus", {
                requestId: requestId,
                status: status,
                paymentUrl: paymentUrl,
            }).then((response) => {
                alert("Payment updated successfully");
            }).catch((error) => {
                console.log(error);
                alert("Something went wrong");
            }).finally(async () => {
                this.requestUpdatingStatuses = [];
                this.fetchRequests();
                await this.paymentUpdateModal.hide();
                this.paymentUpdateModal = null;
            });
        },*/
        openPaymentUpdateModal(requestId) {
            this.paymentUpdateModal = new Modal(document.getElementById(`payment-update-modal-${requestId}`));
            this.paymentUpdateModal.show();
        },
        getRequestLogs(request) {
            return request?.update_logs ?? [];
        },
        openLogModal(requestId) {
            const modal = new Modal(document.getElementById(`request-log-modal-${requestId}`));
            modal.show();
        },
        submitCreditForm(e) {
            e.preventDefault();
            if (this.selectedCredits === null) {
                alert("Please enter credits");
                return;
            }

            const formData = new FormData(this.$refs.creditForm);
            this.updateCreditRequest(formData, this.staffId);
        },
        updateCreditRequest(formData) {
            this.updatingCredits = true;

            this.$store.dispatch("credit/updateRequestCredits", {
                formData: formData,
            }).then((response) => {
                this.fetchRequests();
                alert("Updated successfully");
            }).catch((error) => {
                console.log(error);
                alert("Something went wrong");
            }).finally(() => {
                this.updatingCredits = false;
                this.editRequestModal.hide();
            });
        },
    },
};
</script>

